// src/pages/CRM/SFA/OpportunityPipeline.js
import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
  Box, Typography, Paper, Card, CardContent, Chip, Avatar,
  IconButton, Button, Menu, MenuItem, Tooltip, CircularProgress,
  Alert, TextField, InputAdornment, Stack, Divider, 
  Dialog, DialogTitle, DialogContent, DialogActions, 
  FormControlLabel, Radio, RadioGroup, useTheme
} from '@mui/material';
import {
  ArrowBack as BackIcon, // Added BackIcon
  Add as AddIcon,
  Search as SearchIcon,
  Refresh as RefreshIcon,
  MoreVert as MoreIcon,
  Business as BusinessIcon,
  AttachMoney as MoneyIcon,
  Event as EventIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  ViewList as ListIcon,
  FilterList as FilterIcon,
  TrendingUp as TrendingUpIcon,
  PieChart as PieChartIcon,
  CheckCircle as WonIcon,
  Cancel as LostIcon,
  Warning as WarningIcon
} from '@mui/icons-material';
import { DragDropContext, Droppable, Draggable } from 'react-beautiful-dnd';
import { useSnackbar } from 'notistack';
import { hasPermission } from '../../../../utils/permissionUtils';
import { opportunityService } from './sfaService';
import {
  OpportunityStage, OpportunityStageLabels, OpportunityStageColors,
  formatCurrency, LossReasons, calculateDaysInStage, getStagnantStatus
} from './sfaEnums';

// --- Components ---

const StatCard = ({ title, value, icon, color, subtitle }) => (
  <Paper
    elevation={0}
    sx={{
      p: 2,
      display: 'flex',
      alignItems: 'center',
      gap: 2,
      border: '1px solid',
      borderColor: 'divider',
      borderRadius: 2,
      bgcolor: 'white',
      height: '100%',
      transition: 'transform 0.2s, box-shadow 0.2s',
      '&:hover': {
        transform: 'translateY(-2px)',
        boxShadow: '0 4px 12px rgba(0,0,0,0.05)'
      }
    }}
  >
    <Avatar variant="rounded" sx={{ bgcolor: `${color}15`, color: color, width: 48, height: 48 }}>
      {icon}
    </Avatar>
    <Box>
      <Typography variant="body2" color="text.secondary" fontWeight={600} sx={{ textTransform: 'uppercase', fontSize: '0.7rem', letterSpacing: 0.5 }}>
        {title}
      </Typography>
      <Typography variant="h5" fontWeight={700} sx={{ my: 0.5 }}>
        {value}
      </Typography>
      {subtitle && <Typography variant="caption" color="text.secondary">{subtitle}</Typography>}
    </Box>
  </Paper>
);

const OpportunityPipeline = () => {
  const { chatbotId } = useParams();
  const navigate = useNavigate();
  const { enqueueSnackbar } = useSnackbar();
  const theme = useTheme();

  // State
  const [loading, setLoading] = useState(true);
  const [pipeline, setPipeline] = useState({ stages: [], summary: {} });
  const [searchTerm, setSearchTerm] = useState('');
  
  // Dialogs & Menu
  const [closeDialog, setCloseDialog] = useState({ open: false, opportunity: null, isWon: false, reason: '' });
  const [menuAnchor, setMenuAnchor] = useState(null);
  const [selectedOpp, setSelectedOpp] = useState(null);

  // Permissions
  const [canView, setCanView] = useState(false);
  const [canModify, setCanModify] = useState(false);

  useEffect(() => {
    document.title = 'Pipeline | Sales Force Automation';
    checkPermissions();
  }, []);

  useEffect(() => {
    if (canView) {
      loadPipeline();
    }
  }, [canView, chatbotId]);

  const checkPermissions = async () => {
    try {
      const viewPerm = await hasPermission('crm', 'view');
      const modifyPerm = await hasPermission('crm', 'add|edit');
      setCanView(viewPerm);
      setCanModify(modifyPerm);
    } catch (err) {
      console.error('Error checking permissions:', err);
    }
  };

  const loadPipeline = async () => {
    setLoading(true);
    try {
      const params = chatbotId ? { chatbot_id: chatbotId } : {};
      const response = await opportunityService.getPipeline(params);
      if (response.success) {
        setPipeline(response.data);
      }
    } catch (err) {
      enqueueSnackbar('Failed to load pipeline', { variant: 'error' });
    } finally {
      setLoading(false);
    }
  };

  const handleDragEnd = async (result) => {
    const { destination, source, draggableId } = result;

    if (!destination || (destination.droppableId === source.droppableId && destination.index === source.index)) {
      return;
    }

    const newStage = destination.droppableId;
    const opportunityId = parseInt(draggableId);

    // Check for closing
    if (newStage === OpportunityStage.CLOSED_WON || newStage === OpportunityStage.CLOSED_LOST) {
      const opp = pipeline.stages.find(s => s.stage === source.droppableId)?.opportunities
        .find(o => o.id === opportunityId);
      
      setCloseDialog({
        open: true,
        opportunity: opp,
        isWon: newStage === OpportunityStage.CLOSED_WON,
        reason: ''
      });
      return;
    }

    // Optimistic UI Update
    const newPipeline = { ...pipeline };
    const sourceStage = newPipeline.stages.find(s => s.stage === source.droppableId);
    const destStage = newPipeline.stages.find(s => s.stage === newStage);

    if (sourceStage && destStage) {
      const [movedOpp] = sourceStage.opportunities.splice(source.index, 1);
      movedOpp.stage = newStage;
      destStage.opportunities.splice(destination.index, 0, movedOpp);
      
      // Update totals optimistically (simplified)
      sourceStage.count--; 
      destStage.count++;
      
      setPipeline(newPipeline);
    }

    try {
      await opportunityService.updateStage(opportunityId, { stage: newStage });
      enqueueSnackbar('Stage updated', { variant: 'success', autoHideDuration: 2000 });
      // Reload in background to ensure totals are 100% correct
      loadPipeline(); 
    } catch (err) {
      enqueueSnackbar('Failed to update stage', { variant: 'error' });
      loadPipeline(); // Revert
    }
  };

  const handleCloseOpportunity = async () => {
    const { opportunity, isWon, reason } = closeDialog;
    
    try {
      const stage = isWon ? OpportunityStage.CLOSED_WON : OpportunityStage.CLOSED_LOST;
      const data = { stage, [isWon ? 'win_reason' : 'loss_reason']: reason };

      await opportunityService.updateStage(opportunity.id, data);
      enqueueSnackbar(`Opportunity marked as ${isWon ? 'Won' : 'Lost'}`, { variant: isWon ? 'success' : 'info' });
      setCloseDialog({ open: false, opportunity: null, isWon: false, reason: '' });
      loadPipeline();
    } catch (err) {
      enqueueSnackbar('Failed to close opportunity', { variant: 'error' });
    }
  };

  const handleDeleteOpportunity = async () => {
    if (!selectedOpp) return;
    try {
      await opportunityService.delete(selectedOpp.id);
      enqueueSnackbar('Opportunity deleted', { variant: 'success' });
      setMenuAnchor(null);
      loadPipeline();
    } catch (err) {
      enqueueSnackbar('Failed to delete opportunity', { variant: 'error' });
    }
  };

  // Filter opportunities locally for search
  const filteredStages = pipeline.stages.map(stage => ({
    ...stage,
    opportunities: stage.opportunities.filter(opp => 
      !searchTerm || 
      opp.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
      opp.account?.name?.toLowerCase().includes(searchTerm.toLowerCase())
    )
  }));

  if (!canView) return <Alert severity="error" sx={{ m: 3, mt: { xs: 2, sm: 10 } }}>Access Denied</Alert>;

  return (
    <Box sx={{ height: 'calc(100vh - 100px)', mt: { xs: 2, sm: 10 }, display: 'flex', flexDirection: 'column', bgcolor: '#f4f6f8', overflow: 'hidden' }}>
      
      {/* --- Toolbar --- */}
      <Box
        sx={{
          px: { xs: 1.5, sm: 3 }, pt: 3, pb: 2,
          display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start',
          flexWrap: 'wrap', gap: 2,
          flexShrink: 0
        }}
      >
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <IconButton onClick={() => navigate(-1)} sx={{ bgcolor: 'white', border: 1, borderColor: 'divider', '&:hover': { bgcolor: 'grey.50' } }}>
            <BackIcon />
          </IconButton>
          <Box>
            <Typography variant="h5" fontWeight={700} sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              Pipeline
              <Chip label="Kanban" size="small" color="primary" sx={{ borderRadius: 1, fontWeight: 600, height: 24 }} />
            </Typography>
            <Typography variant="body2" color="text.secondary">
              Manage your deals and sales stages
            </Typography>
          </Box>
        </Box>

        <Stack direction="row" spacing={2} sx={{ flexWrap: 'wrap' }} useFlexGap>
          <TextField
            size="small"
            placeholder="Search deals..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            InputProps={{
              startAdornment: <InputAdornment position="start"><SearchIcon fontSize="small" /></InputAdornment>,
              sx: { bgcolor: 'white', borderRadius: 2, '& fieldset': { borderColor: 'divider' } }
            }}
            sx={{ width: { xs: '100%', sm: 250 } }}
          />
          <Tooltip title="List View">
            <IconButton 
              onClick={() => navigate(`/chatbot/${chatbotId}/crm/opportunities`)} 
              sx={{ bgcolor: 'white', border: 1, borderColor: 'divider', '&:hover': { bgcolor: 'grey.50' } }}
            >
              <ListIcon />
            </IconButton>
          </Tooltip>
          <Tooltip title="Refresh">
            <IconButton 
              onClick={loadPipeline} 
              sx={{ bgcolor: 'white', border: 1, borderColor: 'divider', '&:hover': { bgcolor: 'grey.50' } }}
            >
              <RefreshIcon />
            </IconButton>
          </Tooltip>
          {canModify && (
            <Button
              variant="contained"
              size="small"
              startIcon={<AddIcon />}
              onClick={() => navigate(`/chatbot/${chatbotId}/crm/opportunities/new`)}
              sx={{ borderRadius: 2, px: 3, boxShadow: theme.shadows[2] }}
            >
              New Deal
            </Button>
          )}
        </Stack>
      </Box>

      {/* --- Metrics --- */}
      <Box sx={{ px: { xs: 1.5, sm: 3 }, pb: 3, flexShrink: 0 }}>
        <Stack direction={{ xs: 'column', md: 'row' }} spacing={2}>
          <Box sx={{ flex: 1 }}>
            <StatCard 
              title="Total Pipeline" 
              value={formatCurrency(pipeline.summary?.total_pipeline_value || 0)} 
              subtitle={`${pipeline.summary?.total_count || 0} active deals`}
              icon={<TrendingUpIcon />} 
              color="#2196f3" 
            />
          </Box>
          <Box sx={{ flex: 1 }}>
            <StatCard 
              title="Weighted Forecast" 
              value={formatCurrency(pipeline.summary?.total_weighted_value || 0)} 
              subtitle="Probability adjusted"
              icon={<PieChartIcon />} 
              color="#9c27b0" 
            />
          </Box>
          <Box sx={{ flex: 1 }}>
            <StatCard 
              title="Won This Period" 
              value={formatCurrency(pipeline.summary?.won_value || 0)} 
              subtitle="Closed deals"
              icon={<WonIcon />} 
              color="#4caf50" 
            />
          </Box>
        </Stack>
      </Box>

      {/* --- Kanban Board --- */}
      <Box sx={{ flexGrow: 1, overflowX: 'auto', overflowY: 'hidden', px: { xs: 1.5, sm: 3 }, pb: 3 }}>
        {loading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100%' }}>
            <CircularProgress />
          </Box>
        ) : (
          <DragDropContext onDragEnd={handleDragEnd}>
            <Box sx={{ display: 'flex', height: '100%', gap: 2, minWidth: 'fit-content' }}>
              {filteredStages.map((stage) => (
                <Droppable key={stage.stage} droppableId={stage.stage}>
                  {(provided, snapshot) => (
                    <Paper
                      ref={provided.innerRef}
                      {...provided.droppableProps}
                      elevation={0}
                      sx={{
                        width: 300,
                        minWidth: 300,
                        display: 'flex',
                        flexDirection: 'column',
                        bgcolor: snapshot.isDraggingOver ? 'action.hover' : '#ebecf0', // Trello-like grey
                        borderRadius: 2,
                        maxHeight: '100%'
                      }}
                    >
                      {/* Lane Header */}
                      <Box 
                        sx={{ 
                          p: 1.5, 
                          borderTop: `4px solid ${OpportunityStageColors[stage.stage]}`,
                          borderTopLeftRadius: 8,
                          borderTopRightRadius: 8,
                          bgcolor: 'white',
                          boxShadow: '0 1px 2px rgba(0,0,0,0.05)',
                          flexShrink: 0,
                          mb: 1
                        }}
                      >
                        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 0.5 }}>
                          <Typography variant="subtitle2" fontWeight={700} noWrap>
                            {OpportunityStageLabels[stage.stage]}
                          </Typography>
                          <Chip 
                            label={stage.opportunities.length} 
                            size="small" 
                            sx={{ height: 20, fontSize: '0.7rem', fontWeight: 600, bgcolor: 'grey.100' }} 
                          />
                        </Box>
                        <Typography variant="caption" color="text.secondary" fontWeight={600} display="block">
                          {formatCurrency(stage.total_amount)}
                        </Typography>
                      </Box>

                      {/* Opportunities List */}
                      <Box 
                        sx={{ 
                          px: 1, 
                          pb: 1,
                          flexGrow: 1, 
                          overflowY: 'auto',
                          '::-webkit-scrollbar': { width: 6 },
                          '::-webkit-scrollbar-thumb': { bgcolor: 'rgba(0,0,0,0.1)', borderRadius: 3 }
                        }}
                      >
                        {stage.opportunities.map((opp, index) => (
                          <Draggable key={opp.id} draggableId={String(opp.id)} index={index} isDragDisabled={!canModify}>
                            {(provided, snapshot) => (
                              <Card
                                ref={provided.innerRef}
                                {...provided.draggableProps}
                                {...provided.dragHandleProps}
                                elevation={snapshot.isDragging ? 8 : 0}
                                sx={{
                                  mb: 1,
                                  borderRadius: 2,
                                  cursor: canModify ? 'grab' : 'pointer',
                                  transition: 'all 0.2s',
                                  border: '1px solid',
                                  borderColor: snapshot.isDragging ? 'primary.main' : 'transparent',
                                  boxShadow: snapshot.isDragging ? theme.shadows[4] : '0 1px 2px rgba(0,0,0,0.1)',
                                  position: 'relative',
                                  bgcolor: 'white',
                                  '&:hover': {
                                    transform: 'translateY(-2px)',
                                    boxShadow: '0 4px 8px rgba(0,0,0,0.1)'
                                  }
                                }}
                                onClick={() => navigate(`/chatbot/${chatbotId}/crm/opportunities/${opp.id}`)}
                              >
                                <CardContent sx={{ p: 1.5, '&:last-child': { pb: 1.5 } }}>
                                  <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 1 }}>
                                    <Typography variant="subtitle2" fontWeight={600} sx={{ lineHeight: 1.3, mb: 0.5, fontSize: '0.9rem' }}>
                                      {opp.name}
                                    </Typography>
                                    <IconButton 
                                      size="small" 
                                      onClick={(e) => { e.stopPropagation(); setMenuAnchor(e.currentTarget); setSelectedOpp(opp); }}
                                      sx={{ mt: -0.5, mr: -0.5, p: 0.5 }}
                                    >
                                      <MoreIcon fontSize="small" />
                                    </IconButton>
                                  </Box>

                                  {opp.account && (
                                    <Box sx={{ display: 'flex', alignItems: 'center', mb: 1.5, gap: 0.5 }}>
                                      <BusinessIcon sx={{ fontSize: 14, color: 'text.disabled' }} />
                                      <Typography variant="caption" color="text.secondary" noWrap>
                                        {opp.account.name}
                                      </Typography>
                                    </Box>
                                  )}

                                  {/* Stagnant Deal Alert */}
                                  {(() => {
                                    const daysInStage = calculateDaysInStage(opp.stage_history, opp.stage);
                                    const stagnantStatus = getStagnantStatus(daysInStage);
                                    if (stagnantStatus) {
                                      return (
                                        <Tooltip title={stagnantStatus.message}>
                                          <Chip
                                            icon={<WarningIcon sx={{ fontSize: 14 }} />}
                                            label={stagnantStatus.message}
                                            size="small"
                                            color={stagnantStatus.color}
                                            variant="outlined"
                                            sx={{
                                              height: 20,
                                              fontSize: '0.65rem',
                                              mb: 1,
                                              '& .MuiChip-icon': { fontSize: 14 }
                                            }}
                                          />
                                        </Tooltip>
                                      );
                                    }
                                    return null;
                                  })()}

                                  <Divider sx={{ my: 1, borderStyle: 'dashed' }} />

                                  <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                                    <Chip 
                                      label={formatCurrency(opp.amount)} 
                                      size="small" 
                                      sx={{ 
                                        height: 22, 
                                        fontWeight: 600, 
                                        bgcolor: 'success.50', 
                                        color: 'success.main',
                                        fontSize: '0.75rem',
                                        border: 'none'
                                      }} 
                                    />
                                    {opp.close_date && (
                                      <Tooltip title="Close Date">
                                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                                          <EventIcon sx={{ fontSize: 14, color: 'text.disabled' }} />
                                          <Typography variant="caption" color="text.secondary">
                                            {new Date(opp.close_date).toLocaleDateString(undefined, { month: 'short', day: 'numeric' })}
                                          </Typography>
                                        </Box>
                                      </Tooltip>
                                    )}
                                  </Box>
                                </CardContent>
                              </Card>
                            )}
                          </Draggable>
                        ))}
                        {provided.placeholder}
                      </Box>
                    </Paper>
                  )}
                </Droppable>
              ))}

              {/* Drop Zones for Closed Status */}
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, minWidth: 100 }}>
                <ClosedDropZone stage={OpportunityStage.CLOSED_WON} icon={<WonIcon fontSize="large" />} label="Won" color="success" />
                <ClosedDropZone stage={OpportunityStage.CLOSED_LOST} icon={<LostIcon fontSize="large" />} label="Lost" color="error" />
              </Box>
            </Box>
          </DragDropContext>
        )}
      </Box>

      {/* --- Context Menu --- */}
      <Menu
        anchorEl={menuAnchor}
        open={Boolean(menuAnchor)}
        onClose={() => setMenuAnchor(null)}
      >
        <MenuItem onClick={() => { navigate(`/chatbot/${chatbotId}/crm/opportunities/${selectedOpp?.id}`); setMenuAnchor(null); }}>
          <EditIcon fontSize="small" sx={{ mr: 1 }} /> View Details
        </MenuItem>
        {canModify && (
          <MenuItem onClick={handleDeleteOpportunity} sx={{ color: 'error.main' }}>
            <DeleteIcon fontSize="small" sx={{ mr: 1 }} /> Delete
          </MenuItem>
        )}
      </Menu>

      {/* --- Close Dialog --- */}
      <Dialog open={closeDialog.open} onClose={() => setCloseDialog(prev => ({ ...prev, open: false }))} PaperProps={{ sx: { borderRadius: 2 } }}>
        <DialogTitle sx={{ fontWeight: 700, display: 'flex', alignItems: 'center', gap: 1 }}>
          {closeDialog.isWon ? <WonIcon color="success" /> : <LostIcon color="error" />}
          {closeDialog.isWon ? 'Mark as Won' : 'Mark as Lost'}
        </DialogTitle>
        <DialogContent>
          <Typography variant="body2" sx={{ mb: 2, color: 'text.secondary' }}>
            {closeDialog.isWon 
              ? "Great job! What was the main reason for winning this deal?" 
              : "Please select the primary reason for the lost opportunity."}
          </Typography>
          <FormControlLabel
            value="reason"
            control={<RadioGroup value={closeDialog.reason} onChange={(e) => setCloseDialog(prev => ({ ...prev, reason: e.target.value }))}>
              {(closeDialog.isWon 
                ? ['Price', 'Relationship', 'Product Fit', 'Support', 'Reputation', 'Other'] 
                : LossReasons
              ).map(r => (
                <FormControlLabel key={r} value={r} control={<Radio size="small" />} label={<Typography variant="body2">{r}</Typography>} />
              ))}
            </RadioGroup>}
            label=""
          />
        </DialogContent>
        <DialogActions sx={{ p: 2, pt: 0 }}>
          <Button onClick={() => setCloseDialog(prev => ({ ...prev, open: false }))}>Cancel</Button>
          <Button 
            variant="contained" 
            color={closeDialog.isWon ? 'success' : 'error'} 
            onClick={handleCloseOpportunity}
            disabled={!closeDialog.reason}
          >
            Confirm
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

const ClosedDropZone = ({ stage, icon, label, color }) => (
  <Droppable droppableId={stage}>
    {(provided, snapshot) => (
      <Paper
        ref={provided.innerRef}
        {...provided.droppableProps}
        variant="outlined"
        sx={{
          width: 100,
          flexGrow: 1,
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          justifyContent: 'center',
          borderStyle: 'dashed',
          borderWidth: 2,
          borderColor: snapshot.isDraggingOver ? `${color}.main` : 'divider',
          bgcolor: snapshot.isDraggingOver ? `${color}.50` : 'transparent',
          color: snapshot.isDraggingOver ? `${color}.main` : 'text.disabled',
          transition: 'all 0.2s',
          borderRadius: 3
        }}
      >
        <Box sx={{ opacity: snapshot.isDraggingOver ? 1 : 0.5, textAlign: 'center' }}>
          {icon}
          <Typography variant="caption" fontWeight={700} display="block" sx={{ mt: 1 }}>
            DRAG TO<br/>{label.toUpperCase()}
          </Typography>
        </Box>
        {provided.placeholder}
      </Paper>
    )}
  </Droppable>
);

export default OpportunityPipeline;